import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Layouts
const LayoutWrapper = () => import('@/layouts/LayoutWrapper.vue');
const AuthLayout = () => import('@/layouts/AuthLayout.vue');
const PublicLayout = () => import('@/layouts/PublicLayout.vue');

// Views
const Dashboard = () => import('@/views/Dashboard.vue');
const Backlog = () => import('@/views/Backlog.vue');
const Board = () => import('@/views/Board.vue');
const Schedule = () => import('@/views/Schedule.vue');
const ScheduleUtilisation = () => import('@/views/ScheduleUtilisation.vue');
const ResourceList = () => import('@/views/ResourceList.vue');
const ResourceDetail = () => import('@/views/ResourceDetail.vue');
const ProjectList = () => import('@/views/ProjectList.vue');
const ProjectCreate = () => import('@/views/ProjectCreate.vue');
const ProjectDetail = () => import('@/views/ProjectDetail.vue');
const TaskDetail = () => import('@/views/TaskDetail.vue');
const EpicDetail = () => import('@/views/EpicDetail.vue');
const ProjectTags = () => import('@/views/ProjectTags.vue');
const ConversationView = () => import('@/views/ConversationView.vue');
const GlobalConversations = () => import('@/views/GlobalConversations.vue');
const UserManagement = () => import('@/views/UserManagement.vue');
const SystemSettings = () => import('@/views/SystemSettings.vue');
const Integrations = () => import('@/views/Integrations.vue');
const Login = () => import('@/views/Login.vue');
const PendingApproval = () => import('@/views/PendingApproval.vue');
const TeamList = () => import('@/views/TeamList.vue');
const TeamDetail = () => import('@/views/TeamDetail.vue');
const SeatManagement = () => import('@/views/SeatManagement.vue');
const Profile = () => import('@/views/Profile.vue');
const CustomerList = () => import('@/views/CustomerList.vue');
const BroadcastTemplates = () => import('@/views/BroadcastTemplates.vue');
const CustomerBroadcasts = () => import('@/views/CustomerBroadcasts.vue');
const BroadcastDetail = () => import('@/views/BroadcastDetail.vue');
const SurveyTemplates = () => import('@/views/SurveyTemplates.vue');
const SurveyTemplateEdit = () => import('@/views/SurveyTemplateEdit.vue');
const SurveyInvitationDetail = () => import('@/views/SurveyInvitationDetail.vue');
const PublicSurvey = () => import('@/views/PublicSurvey.vue');
const CustomerDetail = () => import('@/views/CustomerDetail.vue');
const HelpdeskAppList = () => import('@/views/HelpdeskAppList.vue');
const HelpdeskAppCreate = () => import('@/views/HelpdeskAppCreate.vue');
const HelpdeskAppSettings = () => import('@/views/HelpdeskAppSettings.vue');
const HelpdeskTicketList = () => import('@/views/HelpdeskTicketList.vue');
const HelpdeskAllTickets = () => import('@/views/HelpdeskAllTickets.vue');
const HelpdeskTicketDetail = () => import('@/views/HelpdeskTicketDetail.vue');
const Route53Zones = () => import('@/views/Route53Zones.vue');
const Route53Records = () => import('@/views/Route53Records.vue');
const PorkbunDomains = () => import('@/views/PorkbunDomains.vue');
const PorkbunRecords = () => import('@/views/PorkbunRecords.vue');
const SensuDashboard = () => import('@/views/SensuDashboard.vue');
const SensuEventDetail = () => import('@/views/SensuEventDetail.vue');
const LokiLogs = () => import('@/views/LokiLogs.vue');
const PackageList = () => import('@/views/PackageList.vue');
const PackageDetail = () => import('@/views/PackageDetail.vue');
const PullRequests = () => import('@/views/PullRequests.vue');
const WorkflowList = () => import('@/views/WorkflowList.vue');
const ContainerList = () => import('@/views/ContainerList.vue');
const BaseImageDetail = () => import('@/views/BaseImageDetail.vue');
const RedisInstanceList = () => import('@/views/RedisInstanceList.vue');
const RedisInstanceDetail = () => import('@/views/RedisInstanceDetail.vue');
const VulnerabilityMonitor = () => import('@/views/VulnerabilityMonitor.vue');
const VmHostList = () => import('@/views/VmHostList.vue');
const VmHostRegister = () => import('@/views/VmHostRegister.vue');
const VmHostDetail = () => import('@/views/VmHostDetail.vue');
const VmAttackSurface = () => import('@/views/VmAttackSurface.vue');
const VmFindingDetail = () => import('@/views/VmFindingDetail.vue');
const VmCveList = () => import('@/views/VmCveList.vue');
const VmCveDetail = () => import('@/views/VmCveDetail.vue');
const AlertAppList = () => import('@/views/alerts/AlertAppList.vue');
const AlertAppCreate = () => import('@/views/alerts/AlertAppCreate.vue');
const AlertAppSettings = () => import('@/views/alerts/AlertAppSettings.vue');
const AlertDashboard = () => import('@/views/alerts/AlertDashboard.vue');
const AlertAllAlerts = () => import('@/views/alerts/AlertAllAlerts.vue');
const AlertDetail = () => import('@/views/alerts/AlertDetail.vue');
const HeartbeatList = () => import('@/views/alerts/HeartbeatList.vue');
const BlackoutList = () => import('@/views/alerts/BlackoutList.vue');
// Subscription is now part of Profile.vue

const routes = [
  // Auth routes (no layout required, AuthLayout wraps content)
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'login',
        component: Login,
        meta: { guest: true },
      },
    ],
  },
  {
    path: '/pending-approval',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'pending-approval',
        component: PendingApproval,
        meta: { requiresAuth: true, allowPending: true },
      },
    ],
  },

  // Main app routes (with DefaultLayout)
  {
    path: '/',
    component: LayoutWrapper,
    meta: { requiresAuth: true, requiresApproval: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: Dashboard,
      },
      {
        path: 'backlog',
        name: 'backlog',
        component: Backlog,
      },
      {
        path: 'board',
        name: 'board',
        component: Board,
      },
      {
        path: 'schedule',
        name: 'schedule',
        component: Schedule,
      },
      {
        path: 'schedule/utilisation',
        name: 'schedule-utilisation',
        component: ScheduleUtilisation,
      },
      {
        path: 'resources',
        name: 'resources',
        component: ResourceList,
      },
      {
        path: 'resources/:id',
        name: 'resource-detail',
        component: ResourceDetail,
        props: true,
      },
      {
        path: 'pull-requests',
        name: 'pull-requests',
        component: PullRequests,
      },
      {
        path: 'conversations',
        name: 'global-conversations',
        component: GlobalConversations,
      },
      {
        path: 'timeline',
        name: 'timeline',
        component: () => import('@/views/Timeline.vue'),
      },
      {
        path: 'session-monitor',
        name: 'session-monitor',
        component: () => import('@/views/SessionMonitor.vue'),
      },
      {
        // The same live sessions as the board, rendered as a 3D compound. Lazy
        // on purpose — it is the only route that pulls in three.js.
        path: 'session-monitor2',
        name: 'session-foundry',
        component: () => import('@/views/SessionFoundry.vue'),
      },
      {
        path: 'projects',
        name: 'projects',
        component: ProjectList,
      },
      {
        path: 'projects/new',
        name: 'project-create',
        component: ProjectCreate,
      },
      {
        path: 'projects/:key',
        name: 'project-detail',
        component: ProjectDetail,
        props: true,
      },
      {
        path: 'projects/:key/tags',
        name: 'project-tags',
        component: ProjectTags,
        props: true,
      },
      {
        path: 'projects/:key/conversations/:id',
        name: 'conversation-detail',
        component: ConversationView,
        props: true,
      },
      {
        path: 'customers/:customerId/conversations/:id',
        name: 'customer-conversation-detail',
        component: ConversationView,
        props: true,
      },
      {
        path: 'projects/:projectKey/epics/:epicKey',
        name: 'epic-detail',
        component: EpicDetail,
        props: true,
      },
      {
        path: 'tasks/:key',
        name: 'task-detail',
        component: TaskDetail,
        props: true,
      },
      {
        path: 'admin/users',
        name: 'user-management',
        component: UserManagement,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/settings',
        name: 'system-settings',
        component: SystemSettings,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/integrations',
        name: 'integrations',
        component: Integrations,
        meta: { requiresAdmin: true },
      },
      {
        path: 'workflows',
        name: 'workflows',
        component: WorkflowList,
      },
      {
        path: 'containers',
        name: 'containers',
        component: ContainerList,
      },
      {
        path: 'containers/base-images/:image',
        name: 'base-image-detail',
        component: BaseImageDetail,
        props: true,
      },
      {
        path: 'redis',
        name: 'redis-instances',
        component: RedisInstanceList,
      },
      {
        path: 'redis/:id',
        name: 'redis-instance-detail',
        component: RedisInstanceDetail,
        props: true,
      },
      {
        path: 'vulnerabilities',
        name: 'vulnerability-monitor',
        component: VulnerabilityMonitor,
      },
      {
        path: 'vm',
        name: 'vm',
        redirect: '/vm/hosts',
      },
      {
        path: 'vm/hosts',
        name: 'vm-hosts',
        component: VmHostList,
      },
      {
        path: 'vm/hosts/new',
        name: 'vm-host-register',
        component: VmHostRegister,
      },
      {
        path: 'vm/hosts/:id',
        name: 'vm-host-detail',
        component: VmHostDetail,
        props: true,
      },
      {
        path: 'vm/attack-surface',
        name: 'vm-attack-surface',
        component: VmAttackSurface,
      },
      {
        path: 'vm/findings',
        redirect: { name: 'vm-cves' },
      },
      {
        path: 'vm/findings/:id',
        name: 'vm-finding-detail',
        component: VmFindingDetail,
      },
      {
        path: 'vm/cves',
        name: 'vm-cves',
        component: VmCveList,
      },
      {
        path: 'vm/cves/:cveId',
        name: 'vm-cve-detail',
        component: VmCveDetail,
        props: true,
      },
      {
        path: 'admin/route53',
        name: 'route53-zones',
        component: Route53Zones,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/route53/:zoneId',
        name: 'route53-records',
        component: Route53Records,
        props: true,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/porkbun',
        name: 'porkbun-domains',
        component: PorkbunDomains,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/porkbun/:domain',
        name: 'porkbun-records',
        component: PorkbunRecords,
        props: true,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/sensu',
        name: 'sensu-dashboard',
        component: SensuDashboard,
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/sensu/:namespace/events/:entity/:check',
        name: 'sensu-event-detail',
        component: SensuEventDetail,
        props: true,
        meta: { requiresAdmin: true },
      },
      {
        path: 'logs',
        name: 'loki-logs',
        component: LokiLogs,
      },
      {
        path: 'teams',
        name: 'teams',
        component: TeamList,
      },
      {
        path: 'teams/:id',
        name: 'team-detail',
        component: TeamDetail,
        props: true,
      },
      {
        path: 'settings/subscription',
        redirect: '/profile',
      },
      {
        path: 'settings/seats',
        name: 'seats',
        component: SeatManagement,
      },
      {
        path: 'profile',
        name: 'profile',
        component: Profile,
      },
      {
        path: 'settings',
        redirect: '/profile',
      },
      {
        path: 'packages',
        name: 'packages',
        component: PackageList,
      },
      {
        path: 'packages/:name(.+)',
        name: 'package-detail',
        component: PackageDetail,
        props: true,
      },
      {
        path: 'customers',
        name: 'customers',
        component: CustomerList,
      },
      {
        path: 'customers/broadcasts',
        name: 'customer-broadcasts',
        component: CustomerBroadcasts,
      },
      {
        path: 'customers/broadcasts/:id',
        name: 'broadcast-detail',
        component: BroadcastDetail,
        props: true,
      },
      {
        path: 'customers/broadcast-templates',
        name: 'broadcast-templates',
        component: BroadcastTemplates,
      },
      {
        path: 'customers/survey-templates',
        name: 'survey-templates',
        component: SurveyTemplates,
      },
      {
        path: 'customers/survey-templates/:id',
        name: 'survey-template-edit',
        component: SurveyTemplateEdit,
        props: true,
      },
      {
        path: 'customers/surveys/:id',
        name: 'survey-invitation-detail',
        component: SurveyInvitationDetail,
        props: true,
      },
      {
        path: 'customers/:customerId',
        name: 'customer-detail',
        component: CustomerDetail,
        props: true,
      },
      {
        path: 'tickets',
        name: 'all-tickets',
        component: HelpdeskAllTickets,
      },
      {
        path: 'helpdesk',
        name: 'helpdesk',
        component: HelpdeskAppList,
      },
      {
        path: 'helpdesk/new',
        name: 'helpdesk-app-create',
        component: HelpdeskAppCreate,
      },
      {
        path: 'helpdesk/:appId/settings',
        name: 'helpdesk-app-settings',
        component: HelpdeskAppSettings,
        props: true,
      },
      {
        path: 'helpdesk/:appId/tickets',
        name: 'helpdesk-tickets',
        component: HelpdeskTicketList,
        props: true,
      },
      {
        path: 'helpdesk/:appId/tickets/:ticketNumber',
        name: 'helpdesk-ticket-detail',
        component: HelpdeskTicketDetail,
        props: true,
      },
      {
        path: 'alerts',
        name: 'alerts',
        component: AlertAppList,
      },
      {
        path: 'alerts/new',
        name: 'alerts-app-create',
        component: AlertAppCreate,
      },
      {
        path: 'alerts/all',
        name: 'alerts-all',
        component: AlertAllAlerts,
      },
      {
        path: 'alerts/:appSlug/settings',
        name: 'alerts-app-settings',
        component: AlertAppSettings,
        props: true,
      },
      {
        path: 'alerts/:appSlug/heartbeats',
        name: 'alerts-heartbeats',
        component: HeartbeatList,
        props: true,
      },
      {
        path: 'alerts/:appSlug/blackouts',
        name: 'alerts-blackouts',
        component: BlackoutList,
        props: true,
      },
      {
        path: 'alerts/:appSlug/alert/:id',
        name: 'alerts-detail',
        component: AlertDetail,
        props: true,
      },
      {
        path: 'alerts/:appSlug',
        name: 'alerts-dashboard',
        component: AlertDashboard,
        props: true,
      },
    ],
  },

  // Public survey link — no auth, custom minimal layout
  {
    path: '/survey/:customerId/:token',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'public-survey',
        component: PublicSurvey,
        props: true,
      },
    ],
  },

  // Catch-all redirect
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Initialize auth state if not done
  if (!authStore.initialized) {
    await authStore.fetchUser();
  }

  const isAuthenticated = authStore.isAuthenticated;
  const isApproved = authStore.isApproved;
  const isAdmin = authStore.isAdmin;

  // Guest-only routes (login)
  if (to.meta.guest && isAuthenticated) {
    if (isApproved) {
      return next('/');
    }
    return next('/pending-approval');
  }

  // Routes requiring authentication
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      return next('/login');
    }

    // Routes requiring approval
    if (to.matched.some((record) => record.meta.requiresApproval)) {
      if (!isApproved && !to.meta.allowPending) {
        return next('/pending-approval');
      }
    }

    // Admin-only routes
    if (to.meta.requiresAdmin && !isAdmin) {
      return next('/');
    }
  }

  next();
});

export default router;
